/* eslint-env browser */
// The parts About us and Contact us share (owner, 5 Nov 2026: "i like what you did to
// about us page, can you do the same for contact us page"), in the landing page's look:
//   SitePage   the page: skip link, the public header, a <main>, the landing page's footer
//   NightHero  the dark hero with the city streets: pill, h1, a line; children below it
//   AppBand    the orange band with the stores' badges (and any extra action)
//   useSiteWords  t, L (words from /languages first, then the bundled am / om / ti
//              words, then English) and track (no tight letter-spacing for Ethiopic)
// Motion: entrances only, once, within 5 s; none with Reduce Motion.
import React from 'react';
import Header from 'components/Header/Header.js';
import HomeFooter from 'components/Footer/HomeFooter.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, GlobalStyles } from '@mui/material';
import { keyframes } from '@mui/system';
import Reveal from './Reveal';
import StoreButtons from './StoreButtons';
import CityStreets from './CityStreets';
import landingWord from './landingWords';
import { tokens as c, FONT_STACK } from '../../theme';

// The landing page's night shades, and the light orange that reads on them (9:1).
export const NIGHT = '#130C09';
export const NIGHT_2 = '#21150F';
export const NIGHT_3 = '#0C0806';
export const ORANGE_ON_NIGHT = '#FF9A73';
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';

const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
`;
export const still = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };
// Arriving once on load (what needs no scroll to be seen).
export const enter = (delay) => ({ animation: `${rise} 800ms ${EASE} ${delay}ms both`, ...still });

// A small orange link in running text: phone numbers, emails, the call line.
export const textLinkSx = {
  color: c.ACCENT_TEXT,
  fontWeight: 600,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  borderRadius: '4px',
  '&:hover': { color: c.BUTTON_PRIMARY_PRESSED },
  '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 2 },
};

export function useSiteWords() {
  const { t, i18n } = useTranslation();
  // Words from /languages first; until those are pushed, the bundled am / om / ti words, then English.
  const L = (key, en) => t(key, { defaultValue: landingWord(i18n.language, key, en) });
  // Tight tracking suits Latin headings; Ethiopic letters need their own spacing.
  const ethiopic = /^(am|ti)/.test(String(i18n.language || ''));
  const track = (v) => (ethiopic ? 0 : v);
  return { t, i18n, L, track };
}

export const eyebrowSx = (track) => ({ color: c.ACCENT_TEXT, fontWeight: 700, fontSize: 14, letterSpacing: track('0.14em'), textTransform: 'uppercase', mb: 1.5 });
export const h2Sx = (track) => ({ fontWeight: 700, fontSize: { xs: 32, md: 44 }, lineHeight: 1.12, letterSpacing: track('-0.025em'), color: c.INK, textWrap: 'balance' });

const dashboardRoutes = [];

export default function SitePage({ mainId, children, ...rest }) {
  const { L } = useSiteWords();
  return (
    // overflow clip, not hidden, where the browser has it: hidden makes the page a
    // scroll container, and the legal pages' "On this page" list could not stay in view.
    <Box sx={{ bgcolor: c.WHITE, overflowX: 'hidden', '@supports (overflow: clip)': { overflowX: 'clip' }, fontFamily: FONT_STACK }}>
      {/* No browser body margin round the full-width bands; the fixed header never
          hides the control that has the keyboard focus. */}
      <GlobalStyles styles={{ body: { margin: 0 }, html: { scrollPaddingTop: '160px' } }} />
      <Box
        component="a"
        href={`#${mainId}`}
        sx={{
          position: 'fixed', left: 16, top: 16, zIndex: 1400, px: 2, py: 1.5, borderRadius: '10px',
          bgcolor: c.WHITE, color: c.INK, fontWeight: 700, textDecoration: 'none',
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'none' },
          '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 2 },
        }}
      >
        {L('lp_skip', 'Skip to content')}
      </Box>
      <Header
        color="transparent"
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{ height: 50, color: 'white' }}
        {...rest}
      />
      <Box component="main" id={mainId} tabIndex={-1} sx={{ outline: 'none' }}>
        {children}
      </Box>
      <HomeFooter />
    </Box>
  );
}

// The dark hero: the night city, a pill (icon + the page's name), the h1 and one line.
// pb: room below for what overlaps the hero's edge (none when children are cut by it).
export function NightHero({ id, icon, eyebrow, title, sub, pb = 0, children }) {
  const { track } = useSiteWords();
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: c.WHITE,
        background: [
          'radial-gradient(60% 55% at 50% 100%, rgba(241, 80, 26, 0.55) 0%, rgba(241, 80, 26, 0) 70%)',
          'radial-gradient(40% 45% at 0% 0%, rgba(199, 61, 14, 0.35) 0%, rgba(199, 61, 14, 0) 70%)',
          `linear-gradient(170deg, ${NIGHT_2} 0%, ${NIGHT} 60%, ${NIGHT_3} 100%)`,
        ].join(', '),
        // The fixed header (and its 100-px logo) stays clear of the first line.
        pt: { xs: 20, md: 19 },
        pb,
      }}
    >
      <CityStreets focus="50% 35%" align="xMidYMid" />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.75, py: 0.75, mb: 3, borderRadius: '999px', bgcolor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.16)', ...enter(0) }}>
          <Box aria-hidden sx={{ display: 'flex', color: ORANGE_ON_NIGHT, '& svg': { fontSize: 18 } }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(255, 255, 255, 0.9)' }}>{eyebrow}</Typography>
        </Box>
        <Typography
          component="h1"
          id={id}
          sx={{ fontWeight: 700, fontSize: { xs: 38, sm: 50, md: 58 }, lineHeight: 1.06, letterSpacing: track('-0.03em'), mb: 3, textWrap: 'balance', ...enter(80) }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: { xs: 18, md: 20 }, lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.78)', maxWidth: 640, mx: 'auto', textWrap: 'pretty', ...enter(160) }}>
          {sub}
        </Typography>
      </Container>
      {children}
    </Box>
  );
}

// The orange band before the footer: a title, a line, the stores' badges, then children.
export function AppBand({ id, title, sub, children }) {
  const { track } = useSiteWords();
  return (
    <Box component="section" aria-labelledby={id} sx={{ py: { xs: 10, md: 14 }, bgcolor: c.WHITE }}>
      <Container maxWidth="lg">
        <Reveal>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: { xs: '28px', md: '40px' },
              color: c.WHITE,
              textAlign: 'center',
              px: { xs: 2.5, sm: 6, md: 8 },
              py: { xs: 6, md: 8 },
              // The words sit on the button orange (white 5.1:1), as on the landing page's band.
              background: [
                'radial-gradient(55% 75% at 88% 100%, rgba(255, 140, 90, 0.4) 0%, rgba(255, 140, 90, 0) 70%)',
                `linear-gradient(135deg, ${c.BUTTON_PRIMARY} 0%, ${c.BUTTON_PRIMARY} 45%, ${c.ACCENT} 100%)`,
              ].join(', '),
              boxShadow: '0 30px 60px -30px rgba(169, 51, 11, 0.45)',
            }}
          >
            <Typography component="h2" id={id} sx={{ fontWeight: 700, fontSize: { xs: 32, md: 44 }, lineHeight: 1.12, letterSpacing: track('-0.025em'), textWrap: 'balance' }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, mt: 2, mb: 4, mx: 'auto', maxWidth: 560, color: c.WHITE, textWrap: 'pretty' }}>
              {sub}
            </Typography>
            <StoreButtons ring={c.WHITE} sx={{ justifyContent: 'center' }} />
            {children}
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
}
